/* eslint-disable sonarjs/no-duplicate-string */
import {
  ErrorHandling,
  EVENT_APP_FATAL_ERROR,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_REQUEST_EXIT_MONSTER_WORLD,
  FatalErrors,
  iFrameLauncherEvent,
  isUnsupportedDesktopSafari,
  isUnsupportedMobileSafari,
  LauncherEventType,
  Logger,
} from "@classdojo/monsterverse-utils";
import { translate } from "@monsterverse/i18n";
import { getOrCreateRootDiv, Modal } from "@monsterverse/ui";
import { type ComponentChildren, Fragment, type JSX, render } from "preact";

import { setupTranslations } from "../AWSHostURLs";
import { incrementConsecutiveError, resetConsecutiveError } from "../ConsecutiveErrorCounter";
import { postToApp } from "../MessageHandler";
import type { PCAppEventHandler } from "../PCAppEventHandler";
import { getLauncherLatestURL } from "./getLauncherLatestURL";

type ErrorModalProps = {
  title?: string;
  description?: string | JSX.Element[];
  yesText?: string;
  onYesClicked?: () => void;
  noText?: string;
  onNoClicked?: () => void;
  children?: ComponentChildren;
};

const FatalErrorModal: React.FC<{ props: ErrorModalProps }> = ({ props }) => {
  return (
    <Modal
      shown={true}
      titleText={props.title ?? translate("common.errors.defaults.title")}
      descriptionText={props.description}
      onYes={
        props.onYesClicked
          ? { text: props.yesText ?? translate("common.errors.defaults.ok"), onClicked: props.onYesClicked }
          : undefined
      }
      onNo={
        props.onNoClicked
          ? { text: props.noText ?? translate("common.errors.defaults.cancel"), onClicked: props.onNoClicked }
          : undefined
      }
      children={props.children}
    />
  );
};

let fatalErrorTriggered = false;
function renderFatalErrorModal(errorProps: ErrorModalProps) {
  if (fatalErrorTriggered) {
    return;
  }
  fatalErrorTriggered = true;

  render(<FatalErrorModal props={errorProps} />, getOrCreateRootDiv());
}

function tryReloadPage() {
  const url = getLauncherLatestURL();
  if (url) {
    window.location.replace(url);
  } else {
    window.location.reload();
  }
}

function webGLFatalError() {
  Logger.logInfo("FatalErrorModal webGLFatalError");

  renderFatalErrorModal({
    title: translate("common.errors.webgl_support.title"),
    description: translate("common.errors.webgl_support.description"),
    yesText: translate("common.errors.webgl_support.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGLFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function webGL2FatalError() {
  Logger.logInfo("FatalErrorModal webGL2FatalError");

  renderFatalErrorModal({
    title: translate("common.errors.webgl2_support.default.title"),
    description: translate("common.errors.webgl2_support.default.description"),
    yesText: translate("common.errors.webgl2_support.default.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGL2FatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function webGL2iOSFatalError() {
  Logger.logInfo("FatalErrorModal webGL2iOSFatalError");

  const errorDescription = () => {
    return (
      <>
        <Fragment>
          {translate("common.errors.webgl2_support.ios.description")}
          <br />
          <br />
          <a href="https://support.apple.com/en-us/HT204204">
            {translate("common.errors.webgl2_support.ios.link_text")}
          </a>
        </Fragment>
      </>
    );
  };

  renderFatalErrorModal({
    title: translate("common.errors.webgl2_support.ios.title"),
    description: [errorDescription()],
    yesText: translate("common.errors.webgl2_support.ios.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGL2MacOSFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function webGL2MacOSFatalError() {
  Logger.logInfo("FatalErrorModal webGL2MacOSFatalError");

  const errorDescription = () => {
    return (
      <>
        <Fragment>
          {translate("common.errors.webgl2_support.macos.description")}
          <br />
          <br />
          <a href="https://support.apple.com/en-us/HT201541">
            {translate("common.errors.webgl2_support.macos.link_text")}
          </a>
        </Fragment>
      </>
    );
  };

  renderFatalErrorModal({
    title: translate("common.errors.webgl2_support.macos.title"),
    description: [errorDescription()],
    yesText: translate("common.errors.webgl2_support.macos.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGL2MacOSFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function loggedOutUserFatalError() {
  Logger.logInfo("FatalErrorModal loggedOutUserFatalError");

  renderFatalErrorModal({
    title: translate("common.errors.not_logged_in.title"),
    description: translate("common.errors.not_logged_in.description"),
    yesText: translate("common.errors.not_logged_in.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "LoggedOutUserFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function serverIdleTimeoutError() {
  Logger.logInfo("FatalErrorModal serverIdleTimeoutError");

  Logger.incrementMetric("monsterverse.stats.errors.idleTimeout");
  Logger.incrementMetric("monsterverse.stats.errors.totalTimeouts");

  renderFatalErrorModal({
    title: translate("common.errors.idle.title"),
    yesText: translate("common.errors.idle.yes"),
    noText: translate("common.errors.idle.no"),
    onYesClicked: () => tryReloadPage(),
    onNoClicked: () => {
      Logger.incrementMetric("monsterverse.stats.errors.totalExitsFromTimeouts");
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function serverTicketExpiredTimeoutError() {
  Logger.logInfo("FatalErrorModal serverTicketExpiredTimeoutError");

  Logger.incrementMetric("monsterverse.stats.errors.ticketExpiredTimeout");
  Logger.incrementMetric("monsterverse.stats.errors.totalTimeouts");

  renderFatalErrorModal({
    title: translate("common.errors.ticket_expired_timeout.title"),
    description: translate("common.errors.ticket_expired_timeout.description"),
    yesText: translate("common.errors.ticket_expired_timeout.yes"),
    noText: translate("common.errors.ticket_expired_timeout.no"),
    onYesClicked: () => tryReloadPage(),
    onNoClicked: () => {
      Logger.incrementMetric("monsterverse.stats.errors.totalExitsFromTimeouts");
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function serverReconnectTimeoutError() {
  Logger.logInfo("FatalErrorModal serverReconnectTimeoutError");

  Logger.incrementMetric("monsterverse.stats.errors.totalTimeouts");

  renderFatalErrorModal({
    title: translate("common.errors.connection_timeout.title"),
    description: translate("common.errors.connection_timeout.description"),
    yesText: translate("common.errors.connection_timeout.yes"),
    noText: translate("common.errors.connection_timeout.no"),
    onYesClicked: () => tryReloadPage(),
    onNoClicked: () => {
      Logger.incrementMetric("monsterverse.stats.errors.totalExitsFromTimeouts");
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function serverShuttingDownError() {
  Logger.logInfo("FatalErrorModal serverShuttingDownError");

  Logger.incrementMetric("monsterverse.stats.errors.totalTimeouts");

  renderFatalErrorModal({
    title: translate("common.errors.other.title"),
    description: translate("common.errors.other.description"),
    yesText: translate("common.errors.other.yes"),
    noText: translate("common.errors.other.no"),
    onYesClicked: () => tryReloadPage(),
    onNoClicked: () => {
      Logger.incrementMetric("monsterverse.stats.errors.totalExitsFromTimeouts");
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function websocketClosureError(error: FatalErrors, hostname?: string) {
  Logger.logInfo(`FatalErrorModal websocketClosureError`);

  const escalated = error === FatalErrors.WebsocketClosureEscalated;

  // We want to include websocket closure errors in the total timeouts metric.
  Logger.incrementMetric("monsterverse.stats.errors.totalTimeouts");

  const labels = !escalated
    ? {
        title: translate("common.errors.websocket_closure_1.title"),
        yesText: translate("common.errors.websocket_closure_1.yes"),
        noText: translate("common.errors.websocket_closure_1.no"),
        description: translate("common.errors.websocket_closure_1.description"),
      }
    : {
        title: translate("common.errors.websocket_closure_2.title"),
        yesText: translate("common.errors.websocket_closure_2.yes"),
        noText: translate("common.errors.websocket_closure_2.no"),
        description: [
          <div key="websocket-closure-description">
            {translate("common.errors.websocket_closure_2.description")}
            <br />
            <br />
            <a
              href="https://help.classdojo.com/hc/en-us/articles/360048672091-Domains-to-Allow-List"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "underline" }}
            >
              {translate("common.errors.websocket_closure_2.help_page_link")}
            </a>
            {!!hostname && (
              <>
                <br />
                <br />
                <div style={{ fontSize: "16px" }}>
                  {translate("common.errors.websocket_closure_2.help_page_ref")}
                  <br />
                  <div style={{ fontSize: "14px", fontStyle: "italic" }}>{hostname}</div>
                </div>
              </>
            )}
          </div>,
        ],
      };

  renderFatalErrorModal({
    ...labels,
    onYesClicked: () => tryReloadPage(),
    onNoClicked: () => {
      // Reset websocket closure counter when user chooses to exit
      resetConsecutiveError(FatalErrors.WebsocketClosure);

      // This will only increment if the user opted to exit the app in the modal.
      Logger.incrementMetric("monsterverse.stats.errors.totalExitsFromTimeouts");
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function removePlayer() {
  Logger.logInfo(`FatalErrorModal removePlayer`);

  renderFatalErrorModal({
    title: translate("common.errors.removed.title"),
    yesText: translate("common.errors.removed.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "RemovePlayer" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
    children: (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <img
          src={"https://clients.multiplayer.classdojo.com/assets/f/e8185338d7008bf2d9d30f97ed3e0dea/removed.png"}
          style={{ width: "100px" }}
        />
        <div style={{ fontSize: "16px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
          {translate("common.errors.removed.description")}
        </div>
      </div>
    ),
  });
}

function VIPNoPlotsError() {
  Logger.logInfo(`FatalErrorModal VIPNoPlots`);

  renderFatalErrorModal({
    title: "Island Full",
    yesText: "Exit",
    onYesClicked: () => {
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
    children: (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <img
          src={"https://clients.multiplayer.classdojo.com/assets/f/e8185338d7008bf2d9d30f97ed3e0dea/removed.png"}
          style={{ width: "100px" }}
        />
        <div style={{ fontSize: "16px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
          {`VIP cannot join because there are no free plots`}
        </div>
      </div>
    ),
  });
}

function incompatibleBrowserError() {
  Logger.logInfo("FatalErrorModal incompatibleBrowserError");

  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.incompatible_browser.title"),
      description: translate("common.errors.incompatible_browser.description"),
      noText: translate("common.errors.incompatible_browser.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "IncompatibleBrowser" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
    });
  });
}

function bundleNotFoundError() {
  Logger.logInfo("FatalErrorModal bundleNotFoundError");

  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.bundle_not_found.title"),
      description: translate("common.errors.bundle_not_found.description"),
      noText: translate("common.errors.bundle_not_found.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "BundleNotFoundError" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
    });
  });
}

function bundleLoadError(error: FatalErrors, hostname?: string) {
  Logger.logInfo("FatalErrorModal bundleLoadError");

  const escalated = error === FatalErrors.BundleLoadErrorEscalated;

  setupTranslations().finally(() => {
    const labels = !escalated
      ? {
          title: translate("common.errors.bundle_load_error_1.title"),
          description: translate("common.errors.bundle_load_error_1.description"),
          yesText: translate("common.errors.bundle_load_error_1.yes"),
          noText: translate("common.errors.bundle_load_error_1.no"),
        }
      : {
          title: translate("common.errors.bundle_load_error_2.title"),
          yesText: translate("common.errors.bundle_load_error_2.yes"),
          noText: translate("common.errors.bundle_load_error_2.no"),
          description: [
            <div key="bundle-load-error-description">
              {translate("common.errors.bundle_load_error_2.description")}
              <br />
              <br />
              <a
                href="https://help.classdojo.com/hc/en-us/articles/360048672091-Domains-to-Allow-List"
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "underline" }}
              >
                {translate("common.errors.bundle_load_error_2.help_page_link")}
              </a>
              {!!hostname && (
                <>
                  <br />
                  <br />
                  <div style={{ fontSize: "16px" }}>
                    {translate("common.errors.bundle_load_error_2.help_page_ref")}
                    <br />
                    <div style={{ fontSize: "14px", fontStyle: "italic" }}>{hostname}</div>
                  </div>
                </>
              )}
            </div>,
          ],
        };

    if (escalated) {
      // Reset our count after showing this once.
      resetConsecutiveError(FatalErrors.BundleLoadError);
    }

    renderFatalErrorModal({
      ...labels,
      onYesClicked: () => tryReloadPage(),
      onNoClicked: () => {
        // Reset bundle load error counter when user chooses to exit
        resetConsecutiveError(FatalErrors.BundleLoadError);

        Logger.logInfo("Returning user to app", { reason: "BundleLoadError" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
    });
  });
}

function outageError() {
  Logger.logInfo("FatalErrorModal outage");

  //Ensure translations are loaded
  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.outage.title"),
      noText: translate("common.errors.outage.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "Outage" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
      children: (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <img
            src={"https://clients.multiplayer.classdojo.com/assets/f/f67dfbe7808158abeed8462b47aa92d3/outage.png"}
            style={{ width: "100px" }}
          />
          <div style={{ fontSize: "16px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
            {translate("common.errors.outage.description")}
          </div>
        </div>
      ),
    });
  });
}

function webGLContextFailedToInitializeError() {
  Logger.logInfo("FatalErrorModal webGLContextFailedToInitializeError");

  //Ensure translations are loaded
  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.webgl_context_failed_init.title"),
      noText: translate("common.errors.webgl_context_failed_init.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "WebGLFailedToInitialize" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
      children: (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <img
            src={"https://clients.multiplayer.classdojo.com/assets/f/f67dfbe7808158abeed8462b47aa92d3/outage.png"}
            style={{ width: "200px" }}
          />
          <div style={{ fontSize: "20px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
            {translate("common.errors.webgl_context_failed_init.description")}
          </div>
        </div>
      ),
    });
  });
}

function incompatibleIOSVersionError() {
  Logger.logInfo("FatalErrorModal incompatibleIOSVersionError");

  //Ensure translations are loaded
  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.incompatible_os.title"),
      noText: translate("common.errors.incompatible_os.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "IncompatibleIOSVersion" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
      children: (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <img
            src={"https://clients.multiplayer.classdojo.com/assets/f/f67dfbe7808158abeed8462b47aa92d3/outage.png"}
            style={{ width: "200px" }}
          />
          <div style={{ fontSize: "20px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
            {translate("common.errors.incompatible_os.description")}
          </div>
        </div>
      ),
    });
  });
}

function assertImpossible(_p: never): never {
  throw new Error("INCONCEIVABLE!");
}

export function fatalErrorHandler(
  iframeEl: HTMLIFrameElement,
  pcAppEventHandler: PCAppEventHandler,
  error: FatalErrors,
  metadata?: { hostname?: string }
) {
  postMessage(iframeEl, LauncherEventType.RequestHardTerminateGame, {});
  setTimeout(() => {
    iframeEl.remove();
  }, 1000);
  pcAppEventHandler.post({ eventName: EVENT_APP_LOADING_COMPLETE });

  switch (error) {
    case FatalErrors.UserLoggedOut:
      loggedOutUserFatalError();
      break;
    case FatalErrors.NoWebGL2:
      if (isUnsupportedMobileSafari()) {
        webGL2iOSFatalError();
        break;
      }
      if (isUnsupportedDesktopSafari()) {
        webGL2MacOSFatalError();
        break;
      }
      webGL2FatalError();
      break;
    case FatalErrors.NoWebGL:
      webGLFatalError();
      break;
    case FatalErrors.IncompatibleBrowser:
      incompatibleBrowserError();
      break;
    case FatalErrors.ServerIdleTimeout:
      serverIdleTimeoutError();
      break;
    case FatalErrors.ServerTicketExpiredTimeout:
      serverTicketExpiredTimeoutError();
      break;
    case FatalErrors.ServerReconnectTimeout:
      serverReconnectTimeoutError();
      break;
    case FatalErrors.ServerShuttingDown:
      serverShuttingDownError();
      break;
    case FatalErrors.WebsocketClosure:
    case FatalErrors.WebsocketClosureEscalated:
      websocketClosureError(error, metadata?.hostname);
      break;
    case FatalErrors.RemovePlayer:
      removePlayer();
      break;
    case FatalErrors.VIPNoPlots:
      VIPNoPlotsError();
      break;
    case FatalErrors.Outage:
      outageError();
      break;
    case FatalErrors.BundleNotFound:
      bundleNotFoundError();
      break;
    case FatalErrors.BundleLoadError:
    case FatalErrors.BundleLoadErrorEscalated:
      bundleLoadError(error, metadata?.hostname);
      break;
    case FatalErrors.IOS17ContextLoss:
      incompatibleIOSVersionError();
      break;
    case FatalErrors.WebGLContextFailedToInitialize:
      webGLContextFailedToInitializeError();
      break;
    default:
      assertImpossible(error);
  }

  // NOTE: Removed ServerIdleTimeout and ServerTicketExpiredTimeout because they log too often
  // and we're not monitoring this. ServerIdleTimeout is now logged as a metric.
  if (error !== FatalErrors.ServerIdleTimeout && error !== FatalErrors.ServerTicketExpiredTimeout) {
    Logger.logInfo(`Encountered fatal error: ${FatalErrors[error]}`, { reason: FatalErrors[error] });
  }

  // Final step: flush logs to ensure all metrics are sent.
  Logger.flushLogs();
}

export function handleFatalErrors(iframeEl: HTMLIFrameElement, pcAppEventHandler: PCAppEventHandler) {
  pcAppEventHandler.on(EVENT_APP_FATAL_ERROR, (data: unknown) => {
    // Support both old format (just enum) and new format (object with metadata)
    let error: FatalErrors;
    let metadata: { hostname?: string } | undefined;

    if (data && typeof data === "object" && "error" in data) {
      // New format - object with error and optional metadata
      const errorData = data as { error: FatalErrors; hostname?: string };
      error = errorData.error;
      metadata = errorData.hostname ? { hostname: errorData.hostname } : undefined;
    } else {
      // Fallback for the original format.
      error = data as FatalErrors;
    }

    // Handle bundle load error escalation
    if (error === FatalErrors.BundleLoadError) {
      const consecutiveCount = incrementConsecutiveError(FatalErrors.BundleLoadError);
      error = consecutiveCount > 1 ? FatalErrors.BundleLoadErrorEscalated : FatalErrors.BundleLoadError;
    } else {
      // Reset BundleLoadError counter when any other error occurs
      // This prevents false escalation when different errors happen
      resetConsecutiveError(FatalErrors.BundleLoadError);
    }

    fatalErrorHandler(iframeEl, pcAppEventHandler, error, metadata);
  });

  const fatalError = (errorCase: FatalErrors | { error: FatalErrors; hostname: string }) => {
    // Pass the errorCase directly to the event system - it will handle both formats
    pcAppEventHandler.post({ eventName: EVENT_APP_FATAL_ERROR, eventValues: [errorCase] });
  };

  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.UserLoggedOut, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.IncompatibleBrowser, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerIdleTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerReconnectTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerTicketExpiredTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerShuttingDown, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.RemovePlayer, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.VIPNoPlots, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.WebsocketClosure, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.WebsocketClosureEscalated, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.Outage, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.BundleNotFound, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.BundleLoadError, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.BundleLoadErrorEscalated, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.IOS17ContextLoss, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.NoWebGL2, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.WebGLContextFailedToInitialize, fatalError);
}

function postMessage(iframeEl: HTMLIFrameElement, type: LauncherEventType, data: Record<string, unknown>) {
  iframeEl?.contentWindow?.postMessage({ iFrameLauncherEvent, type, data }, "*");
}
